import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Map, { Marker } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { ArrowLeft, ExternalLink, Link2, Linkedin, MapPin, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import { getStartup } from '../services/api';
import { linkedinCompanyUrl } from '../lib/linkedin';
import { toast } from '../components/ui/sonner';

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center text-[12px] font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 ${className}`}>
    {children}
  </span>
);

const Section = ({ label, children }) => (
  <div className="mt-8">
    <div className="text-[11px] font-bold tracking-[0.2em] text-slate-400 uppercase mb-3">{label}</div>
    {children}
  </div>
);

export default function CompanyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [startup, setStartup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let ok = true;
    setLoading(true);
    setNotFound(false);
    getStartup(id)
      .then((s) => { if (ok) { setStartup(s); setLoading(false); } })
      .catch(() => { if (ok) { setNotFound(true); setLoading(false); } });
    return () => { ok = false; };
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — share it anywhere!');
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-slate-400" size={26} />
      </div>
    );
  }

  if (notFound || !startup) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4 px-6 text-center">
        <p className="text-slate-600">This company could not be found.</p>
        <Link to="/" className="text-emerald-700 font-medium">← Back to the map</Link>
      </div>
    );
  }

  const founders = startup.founders || [];
  const investors = startup.investors || [];
  const website = (startup.website || '').replace(/^https?:\/\//, '');
  const li = linkedinCompanyUrl(startup);
  const hasCoords = typeof startup.lat === 'number' && typeof startup.lng === 'number';

  return (
    <div className="min-h-screen bg-white flex flex-col pb-16" data-testid="company-profile-page">
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-4">
        <button
          onClick={() => navigate('/')}
          data-testid="profile-back-top"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Back to the map
        </button>
      </div>

      <div className="w-full max-w-lg mx-auto px-5">
        <div className="pt-8 flex flex-col items-center text-center gap-3">
          <Logo startup={startup} size={80} />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{startup.name}</h1>
          <div className="flex flex-wrap justify-center gap-2">
            {startup.sector && <Badge>{startup.sector}</Badge>}
            {startup.badge && <Badge>{startup.badge}</Badge>}
            {startup.yc && <Badge className="bg-orange-500 text-white border-orange-500">Y Combinator</Badge>}
          </div>
          {startup.description && (
            <p className="text-sm text-slate-600 leading-relaxed mt-1">{startup.description}</p>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6">
          {website && (
            <a
              href={`https://${website}`}
              target="_blank"
              rel="noreferrer"
              data-testid="profile-website-btn"
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full py-3 text-sm font-semibold shadow-md"
            >
              Visit website <ExternalLink size={15} />
            </a>
          )}
          <button
            onClick={share}
            data-testid="profile-share-btn"
            className={`inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-full px-4 py-3 text-sm font-semibold ${website ? 'shrink-0' : 'flex-1 justify-center'}`}
          >
            <Link2 size={15} /> Share
          </button>
        </div>

        <Section label="Founders">
          {founders.length ? (
            <div className="flex flex-col gap-2">
              {founders.map((f, i) => (
                <div key={i} className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 bg-slate-50/50">
                  <span className="text-sm font-medium text-slate-800">{f.name}</span>
                  {f.linkedin && (
                    <a href={f.linkedin} target="_blank" rel="noreferrer" aria-label={`${f.name} on LinkedIn`} className="text-[#0A66C2]">
                      <Linkedin size={18} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No founders listed yet.</p>
          )}
        </Section>

        <Section label="Investors">
          {investors.length ? (
            <div className="flex flex-wrap gap-2">
              {investors.map((inv, i) => (
                <span key={i} className="text-[13px] px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{inv}</span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">—</p>
          )}
        </Section>

        {li && (
          <Section label="LinkedIn">
            <a href={li} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#0A66C2] text-sm font-medium">
              <Linkedin size={16} /> Company on LinkedIn
            </a>
          </Section>
        )}

        <Section label="Office">
          <div className="flex items-start gap-2 text-sm text-slate-600 mb-3">
            <MapPin size={15} className="text-rose-500 mt-0.5 shrink-0" fill="currentColor" />
            <span>{startup.address}</span>
          </div>
          {hasCoords && (
            <div className="w-full h-48 rounded-2xl overflow-hidden border border-slate-200" data-testid="profile-mini-map">
              <Map
                initialViewState={{ longitude: startup.lng, latitude: startup.lat, zoom: 14 }}
                mapStyle={MAP_STYLE}
                interactive={false}
                attributionControl={false}
                preserveDrawingBuffer
                onLoad={(e) => { const m = e.target; m.resize(); setTimeout(() => m.resize(), 250); }}
                style={{ width: '100%', height: '100%' }}
              >
                <Marker longitude={startup.lng} latitude={startup.lat} anchor="bottom">
                  <div className="w-9 h-9 rounded-xl border-2 border-white shadow-lg overflow-hidden bg-white flex items-center justify-center">
                    <Logo startup={startup} size={36} />
                  </div>
                </Marker>
              </Map>
            </div>
          )}
        </Section>

        <div className="mt-10 flex items-center justify-between text-sm">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium">
            <ArrowLeft size={16} /> Back to the map
          </button>
        </div>
      </div>
    </div>
  );
}
